import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Logo from '@/components/feature/Logo';
import BookingModal from '@/pages/recherche/components/BookingModal';
import PractitionerHero from '@/pages/praticien/components/PractitionerHero';
import PractitionerInfo from '@/pages/praticien/components/PractitionerInfo';
import BookingSidebar from '@/pages/praticien/components/BookingSidebar';
import PractitionerReviews, { type Review } from '@/pages/praticien/components/PractitionerReviews';
import { fetchPractitionerById } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';
import { mapPractitionerRow } from '@/lib/practitioner';
import { buildPhysicianSchema } from '@/lib/schema';
import type { Practitioner } from '@/mocks/practitioners';

export default function PraticienPage() {
  const { id } = useParams();

  const [practitioner, setPractitioner] = useState<Practitioner | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [booking, setBooking] = useState(false);

  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState('');

  const fetchPractitioner = async () => {
    if (!id) return;
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchPractitionerById(id);
      setPractitioner(mapPractitionerRow(data as Record<string, unknown>));
    } catch (err) {
      setPractitioner(null);
      setLoadError(err instanceof Error ? err.message : 'Erreur de chargement de la fiche');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    setReviewsError('');
    setReviews([]);
    setReviewsLoading(false);
  };

  useEffect(() => {
    fetchPractitioner();
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Suivi d'audience : consultation d'une fiche praticien.
  useEffect(() => {
    if (!practitioner) return;
    trackEvent('profile_viewed', {
      specialty: practitioner.type,
      subscription_tier: practitioner.subscriptionTier ?? '',
    });
  }, [practitioner]);

  // Titre spécifique à la fiche et données structurées Schema.org (Physician).
  useEffect(() => {
    if (!practitioner) return;

    document.title = practitioner.type
      ? `${practitioner.name} — ${practitioner.type} à Rabat | MEDICACLIC`
      : `${practitioner.name} — Praticien de santé à Rabat | MEDICACLIC`;

    const scriptId = 'practitioner-jsonld';
    document.getElementById(scriptId)?.remove();
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(buildPhysicianSchema(practitioner));
    document.head.appendChild(script);

    return () => script.remove();
  }, [practitioner]);

  return (
    <div className="min-h-screen bg-background-100">
      <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="px-4 md:px-8 lg:px-12 h-20 flex items-center justify-between">
          <Link to="/">
            <Logo />
          </Link>
          <Link
            to="/recherche"
            className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-md bg-foreground-950 hover:bg-foreground-800 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center" />
            Retour à la recherche
          </Link>
        </div>
      </header>

      <main className="px-4 md:px-8 lg:px-12 py-10 max-w-7xl mx-auto">
        {loading ? (
          <div className="text-center py-24">
            <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
            <p className="text-sm text-foreground-600 mt-4">Chargement de la fiche…</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-20 bg-background-50 rounded-2xl border border-background-200">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-5">
              <i className="ri-error-warning-line text-3xl text-accent-700 w-8 h-8 flex items-center justify-center" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
              Impossible de charger la fiche
            </h3>
            <p className="text-sm text-foreground-600 max-w-md mx-auto mb-6 leading-relaxed">
              {loadError}
            </p>
            <button
              onClick={fetchPractitioner}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line w-4 h-4 flex items-center justify-center" />
              Réessayer
            </button>
          </div>
        ) : !practitioner ? (
          <div className="text-center py-20 bg-background-50 rounded-2xl border border-background-200">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 mb-5">
              <i className="ri-emotion-sad-line text-3xl text-secondary-700 w-8 h-8 flex items-center justify-center" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
              Praticien introuvable
            </h3>
            <p className="text-sm text-foreground-600 max-w-md mx-auto mb-6 leading-relaxed">
              Cette fiche n'existe pas ou n'est plus disponible.
            </p>
            <Link
              to="/recherche"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line w-4 h-4 flex items-center justify-center" />
              Retour à la recherche
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            <nav className="text-xs text-foreground-500 flex items-center gap-1.5">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">Accueil</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
              <Link to="/recherche" className="hover:text-primary-600 cursor-pointer">Recherche</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
              <span className="text-foreground-800">{practitioner.name}</span>
            </nav>

            <PractitionerHero practitioner={practitioner} onBook={() => setBooking(true)} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
              <div className="lg:col-span-2 space-y-6">
                <PractitionerInfo practitioner={practitioner} />
                <PractitionerReviews
                  practitioner={practitioner}
                  reviews={reviews}
                  loading={reviewsLoading}
                  loadError={reviewsError}
                  onRetry={fetchReviews}
                />
              </div>
              <BookingSidebar practitioner={practitioner} onBook={() => setBooking(true)} />
            </div>
          </div>
        )}
      </main>

      {booking && practitioner && (
        <BookingModal practitioner={practitioner} onClose={() => setBooking(false)} />
      )}
    </div>
  );
}